import {
    EEffectAlgn, IEffectModelValCollection, IReflectionModelVal,
} from '@src/slide/domain/ui-model/element-modeles/property/effect/effect.interface';
import {
    alphaToEmu, alphaToOpacity, angleToEmu, emuToAlpha,
    emuToAngle, emuToPercent, emuToPt, opacityToAlpha, percentToEmu, ptToEmu,
} from '@src/slide/infrastructure/module/independent/unit';
import { ICTEffectList, ICTReflectionEffect, STRectAlignment, DeltaOf } from '@tencent/slide-sdk-model';
import { DeepPartial, DeepReadonly } from 'ts-essentials';
import { ALGN_VER3_MAP } from '@src/slide/domain/ui-model/converter/node-property/sdk-ui-enum-map/algn';
import { invertMap } from '@src/slide/infrastructure/module/independent/object/map-invert';

/**
 * 效果 - 映像
 */
export class ReflectionConverter {
    /**
     * 映像的sdk -> ui 映射
     * @param sdkData
     * @param uiData
     */
    public static convertPartialDataFromSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTEffectList>>,
        uiData: IEffectModelValCollection,
    ): void {
        if (sdkData.reflection && Object.keys(sdkData.reflection).length) {
            const algn = ReflectionConverter.convertAlgnFromSdkToUi(sdkData.reflection);
            const blurRad = ReflectionConverter.convertBlurRadFromSdkToUi(sdkData.reflection);
            const dir = ReflectionConverter.convertDirFromSdkToUi(sdkData.reflection);
            const dist = ReflectionConverter.convertDistFromSdkToUi(sdkData.reflection);
            const startOpacity = ReflectionConverter.convertStartOpacityFromSdkToUi(sdkData.reflection);
            const endOpacity = ReflectionConverter.convertEndOpacityFromSdkToUi(sdkData.reflection);
            const size = ReflectionConverter.convertSizeFromSdkToUi(sdkData.reflection);
            const sy = ReflectionConverter.convertSyFromSdkToUi(sdkData.reflection);
            const rotWithShape = ReflectionConverter.convertRotWithShapeFromSdkToUi(sdkData.reflection);

            uiData.reflection = {
                algn,
                blurRad,
                dir,
                dist,
                startOpacity,
                endOpacity,
                size,
                sy,
                rotWithShape,
            };
            // 清掉值是undefined的属性
            Object.keys(uiData.reflection || {}).forEach((p) => {
                if (uiData.reflection && uiData.reflection[p] === undefined) {
                    delete uiData.reflection[p];
                }
            });
        } else {
            delete uiData.reflection;
        }
    }

    /**
     * 映像的ui-> sdk 映射
     * @param uiData
     * @param sdkData
     */
    public static convertPartialDataFromUiToSdk(
        uiData: DeepPartial<IEffectModelValCollection>,
        sdkData: DeltaOf<ICTEffectList>,
    ): void {
        if (uiData.reflection) {
            const reflection = {
                algn: ReflectionConverter.convertAlgnFromUiToSdk(uiData.reflection),
                blurRad: ReflectionConverter.convertBlurRadFromUiToSdk(uiData.reflection),
                dir: ReflectionConverter.convertDirFromUiToSdk(uiData.reflection),
                dist: ReflectionConverter.convertDistFromUiToSdk(uiData.reflection),
                rotWithShape: ReflectionConverter.convertRotWithShapeFromUiToSdk(uiData.reflection),
                stA: ReflectionConverter.convertStartOpacityFromUiToSdk(uiData.reflection),
                endA: ReflectionConverter.convertEndOpacityFromUiToSdk(uiData.reflection),
                endPos: ReflectionConverter.convertSizeFromUiToSdk(uiData.reflection),
                sy: ReflectionConverter.convertSyFromUiToSdk(uiData.reflection),
            };
            // 清掉值是undefined的属性
            Object.keys(reflection).forEach((p) => {
                if (reflection && reflection[p] === undefined) {
                    delete reflection[p];
                }
            });
            sdkData.reflection = reflection;
        } else if (uiData.reflection === null) {
            sdkData.reflection = null;
        }
    }

    // algn
    private static convertAlgnFromSdkToUi(sdkData?:
    DeepReadonly<DeepPartial<ICTReflectionEffect>> | null): EEffectAlgn | undefined {
        if (sdkData?.algn) {
            return ALGN_VER3_MAP.get(sdkData.algn);
        }
    }

    // blurRad
    private static convertBlurRadFromSdkToUi(sdkData?:
    DeepReadonly<DeepPartial<ICTReflectionEffect>> | null): number {
        if (sdkData?.blurRad || sdkData?.blurRad === 0) {
            return emuToPt(sdkData.blurRad);
        }
        return 0;
    }

    // dir
    private static convertDirFromSdkToUi(sdkData?:
    DeepReadonly<DeepPartial<ICTReflectionEffect>> | null): number {
        if (sdkData?.dir || sdkData?.dir === 0) {
            return Math.round(emuToAngle(sdkData.dir));
        }
        return 0;
    }

    // dist
    private static convertDistFromSdkToUi(sdkData?:
    DeepReadonly<DeepPartial<ICTReflectionEffect>> | null): number {
        if (sdkData?.dist || sdkData?.dist === 0) {
            return emuToPt(sdkData.dist);
        }
        return 0;
    }

    // startOpacity
    private static convertStartOpacityFromSdkToUi(sdkData?:
    DeepReadonly<DeepPartial<ICTReflectionEffect>> | null): number {
        if (sdkData?.stA || sdkData?.stA === 0) {
            return Math.round(alphaToOpacity(emuToAlpha(sdkData.stA)));
        }
        return 0;
    }

    // endOpacity
    private static convertEndOpacityFromSdkToUi(sdkData?:
    DeepReadonly<DeepPartial<ICTReflectionEffect>> | null): number | undefined {
        if (sdkData?.endA || sdkData?.endA === 0) {
            return parseFloat(alphaToOpacity(emuToAlpha(sdkData.endA)).toFixed(1));
        }
    }

    // size
    private static convertSizeFromSdkToUi(sdkData?:
    DeepReadonly<DeepPartial<ICTReflectionEffect>> | null): number {
        if (sdkData?.endPos || sdkData?.endPos === 0) {
            return emuToPercent(sdkData.endPos);
        }
        return 100;
    }

    // sy
    private static convertSyFromSdkToUi(sdkData?:
    DeepReadonly<DeepPartial<ICTReflectionEffect>> | null): number {
        if (sdkData?.sy || sdkData?.sy === 0) {
            return sdkData.sy / 100000;
        }
        return 0;
    }

    // rotWithShape
    private static convertRotWithShapeFromSdkToUi(sdkData?:
    DeepReadonly<DeepPartial<ICTReflectionEffect>> | null): number | undefined {
        if (sdkData?.rotWithShape === true || sdkData?.rotWithShape === false) {
            return sdkData.rotWithShape ? 1 : 0;
        }
    }

    // algn
    private static convertAlgnFromUiToSdk(uiData?:
    DeepPartial<IReflectionModelVal> | null): STRectAlignment | undefined {
        if (uiData?.algn) {
            return invertMap(ALGN_VER3_MAP).get(uiData.algn);
        }
    }

    // blurRad
    private static convertBlurRadFromUiToSdk(uiData?:
    DeepPartial<IReflectionModelVal> | null): number | undefined {
        if (uiData?.blurRad || uiData?.blurRad === 0) {
            return ptToEmu(uiData.blurRad);
        }
    }

    // dir
    private static convertDirFromUiToSdk(uiData?:
    DeepPartial<IReflectionModelVal> | null): number | undefined {
        if (uiData?.dir || uiData?.dir === 0) {
            return angleToEmu(uiData.dir);
        }
    }

    // dist
    private static convertDistFromUiToSdk(uiData?:
    DeepPartial<IReflectionModelVal> | null): number | undefined {
        if (uiData?.dist || uiData?.dist === 0) {
            return ptToEmu(uiData.dist);
        }
    }

    // rotWithShape
    private static convertRotWithShapeFromUiToSdk(uiData?:
    DeepPartial<IReflectionModelVal> | null): boolean | undefined {
        if (uiData?.rotWithShape === 1 || uiData?.rotWithShape === 0) {
            return !!uiData.rotWithShape;
        }
    }

    // startOpacity
    private static convertStartOpacityFromUiToSdk(uiData?:
    DeepPartial<IReflectionModelVal> | null): number | undefined {
        if (uiData?.startOpacity || uiData?.startOpacity === 0) {
            return alphaToEmu(opacityToAlpha(uiData.startOpacity));
        }
    }

    // endOpacity
    private static convertEndOpacityFromUiToSdk(uiData?:
    DeepPartial<IReflectionModelVal> | null): number | undefined {
        if (uiData?.endOpacity || uiData?.endOpacity === 0) {
            return alphaToEmu(opacityToAlpha(uiData.endOpacity));
        }
    }

    // size
    private static convertSizeFromUiToSdk(uiData?:
    DeepPartial<IReflectionModelVal> | null): number | undefined {
        if (uiData?.size || uiData?.size === 0) {
            return percentToEmu(uiData.size);
        }
    }

    // sy
    private static convertSyFromUiToSdk(uiData?:
    DeepPartial<IReflectionModelVal> | null): number | undefined {
        if (uiData?.sy || uiData?.sy === 0) {
            return uiData.sy * 100000;
        }
    }
}
